import React, { useState, useEffect, useRef } from "react";
import { VITE_SUPABASE_URL } from "@/utils/const"
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

// Component imports
import StatusCard from "@/components/doula/profile/StatusCard";
import PersonalInformationCard from "@/components/doula/profile/PersonalInformationCard";
import ProfessionalInformationCard from "@/components/doula/profile/ProfessionalInformation";
import ProfilePreferencesCard from "@/components/doula/profile/ProfilePreferencesCard";
import IntegrationsCard from "@/components/doula/profile/IntegrationsCard";
import PackagesCard from "@/components/doula/profile/PackagesCard";
import CertificationsCard from "@/components/doula/profile/CertificationsCard";
import AvailabilityCard from "@/components/doula/profile/AvailabilityCard";
import ProviderExclusionsCard from "@/components/doula/profile/ProviderExclusionsCard";

// Type imports (interfaces are now in the types file)
import type {
	DoulaListing,
	UserInfo,
	Package,
	Certification,
	Provider,
} from "@/components/doula/profile/types";


export default function DoulaProfileManagement() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [uploadingPhoto, setUploadingPhoto] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [itemToDelete, setItemToDelete] = useState<{
		type: string;
		id: string;
		name: string;
	} | null>(null);
	const [savingMonths, setSavingMonths] = useState(false);

	// Main state
	const [listing, setListing] = useState<DoulaListing | null>(null);
	const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
	const [packages, setPackages] = useState<Package[]>([]);
	const [certifications, setCertifications] = useState<Certification[]>([]);
	const [excludedProviders, setExcludedProviders] = useState<Provider[]>([]);

	// Form state
	const [newService, setNewService] = useState("");
	const [newPackage, setNewPackage] = useState({
		name: "",
		description: "",
		price: 0,
		discount_percentage: 0,
	});
	const [newCertification, setNewCertification] = useState({
		name: "",
		description: "",
		issue_date: "",
		certificate_url: "",
	});
	const [editingPackage, setEditingPackage] = useState<Package | null>(null);
	const [editingCertification, setEditingCertification] =
		useState<Certification | null>(null);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editCertDialogOpen, setEditCertDialogOpen] = useState(false);
	const [uploadingCertificate, setUploadingCertificate] = useState(false);

	// Months available state
	const [monthsAvailable, setMonthsAvailable] = useState({
		january: true,
		february: true,
		march: true,
		april: true,
		may: true,
		june: true,
		july: true,
		august: true,
		september: true,
		october: true,
		november: true,
		december: true,
	});

	// Intro call availability state
	const [introDays, setIntroDays] = useState({
		monday: { available: false, startTime: "09:00", endTime: "17:00" },
		tuesday: { available: false, startTime: "09:00", endTime: "17:00" },
		wednesday: { available: false, startTime: "09:00", endTime: "17:00" },
		thursday: { available: false, startTime: "09:00", endTime: "17:00" },
		friday: { available: false, startTime: "09:00", endTime: "17:00" },
		saturday: { available: false, startTime: "09:00", endTime: "17:00" },
		sunday: { available: false, startTime: "09:00", endTime: "17:00" },
	});

	// Google Places ref for excluded provider input (autocomplete lives in ProviderExclusionsCard)
	const inputRef = useRef<HTMLInputElement>(null);

	// Persist the full excluded providers list via the edge function
	const saveExcludedProviders = async (providers: Provider[]) => {
		const response = await fetch(
			`${VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
				},
				body: JSON.stringify({
					step: "excluded_providers",
					data: {
						excluded_providers: {
							providers,
						},
					},
				}),
			},
		);

		const result = await response.json();
		if (!result.success) {
			throw new Error(result.error || "Failed to save excluded providers");
		}
	};

	// Handle adding excluded provider (ProviderExclusionsCard already builds the Provider from the selected place)
	const handleAddProvider = async (provider: Provider) => {
		// Check if provider is already excluded
		const isAlreadyExcluded = excludedProviders.some(
			(p) => p.place_id === provider.place_id,
		);
		if (isAlreadyExcluded) {
			alert("This provider is already in your excluded list.");
			return;
		}

		const updatedProviders = [...excludedProviders, provider];
		setExcludedProviders(updatedProviders);

		try {
			await saveExcludedProviders(updatedProviders);
		} catch (error) {
			console.error("Error adding provider:", error);
			alert("Failed to add provider. Please try again.");
			setExcludedProviders(excludedProviders);
		}
	};

	// Handle removing excluded provider
	const handleRemoveProvider = async (index: number) => {
		const newProviders = [...excludedProviders];
		newProviders.splice(index, 1);
		setExcludedProviders(newProviders);

		try {
			await saveExcludedProviders(newProviders);
		} catch (error) {
			console.error("Error removing provider:", error);
			alert("Failed to remove provider. Please try again.");
			setExcludedProviders(excludedProviders);
		}
	};

	// Load profile data using edge function
	const loadProfileData = async () => {
		if (!user) return;

		try {
			setLoading(true);

			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/get-doula-onboarding-data`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
				},
			);

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to load profile data");
			}

			// Set the data from the edge function response
			if (result.data.user) {
				setUserInfo(result.data.user);
			}

			if (result.data.doula_listing) {
				setListing(result.data.doula_listing);
				// Sync months available with listing data
				if (result.data.doula_listing.months_available) {
					setMonthsAvailable(result.data.doula_listing.months_available);
				}
				// Sync intro call availability with listing data
				if (result.data.doula_listing.availability_guardrails) {
					setIntroDays(result.data.doula_listing.availability_guardrails);
				}
			}

			if (result.data.packages) {
				setPackages(result.data.packages);
			}

			if (result.data.certifications) {
				setCertifications(result.data.certifications);
			}

			if (result.data.excluded_providers) {
				// Convert excluded providers to match our interface
				const providers: Provider[] = result.data.excluded_providers.map(
					(ep: any) => ({
						name: ep.provider_name,
						address: ep.provider_address,
						place_id: ep.provider_place_id,
						types: ep.provider_types || [],
						formatted_address: ep.provider_formatted_address,
						city: ep.provider_city,
						state: ep.provider_state,
						zip: ep.provider_zip,
						latitude: ep.provider_latitude,
						longitude: ep.provider_longitude,
					}),
				);
				setExcludedProviders(providers);
			}
		} catch (error) {
			console.error("Error loading profile data:", error);
		} finally {
			setLoading(false);
		}
	};

	// Handle photo upload using edge function
	const handlePhotoUpload = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const file = event.target.files?.[0];
		if (!file || !listing) return;

		try {
			setUploadingPhoto(true);

			// Create FormData for file upload
			const formData = new FormData();
			formData.append("file", file);

			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/upload-profile-photo`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: formData,
				},
			);

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to upload photo");
			}

			// Update listing with new photo URL
			setListing((prev: DoulaListing | null) =>
				prev ? { ...prev, profile_photo_url: result.url } : null,
			);
		} catch (error) {
			console.error("Failed to upload photo:", error);
			alert("Failed to upload photo. Please try again.");
		} finally {
			setUploadingPhoto(false);
		}
	};

	// Handle save profile
	const handleSaveProfile = async () => {
		if (!listing || !userInfo) return;

		try {
			setSaving(true);

			// Update user info via direct call (users table)
			const { error: userError } = await supabase
				.from("users")
				.update({
					full_name: userInfo.full_name,
				})
				.eq("id", userInfo.id);

			if (userError) throw userError;

			// Update listing via edge function
			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "profile_update",
						data: {
							...listing,
							availability_guardrails: introDays,
							months_available: monthsAvailable,
						},
					}),
				},
			);

			const result = await response.json();
			if (!result.success) throw new Error(result.error);

			alert("Profile updated successfully!");
		} catch (error) {
			console.error("Error saving profile:", error);
			alert("Failed to save profile. Please try again.");
		} finally {
			setSaving(false);
		}
	};

	useEffect(() => {
		loadProfileData();
	}, [user]);

	const toggleSelectAllMonths = () => {
		const allSelected = Object.values(monthsAvailable).every((v) => v);
		const newMonthsAvailable = Object.keys(monthsAvailable).reduce(
			(acc, month) => ({ ...acc, [month]: !allSelected }),
			{} as typeof monthsAvailable,
		);
		setMonthsAvailable(newMonthsAvailable);
	};

	// Loading state
	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100">
				<div className="flex items-center justify-center min-h-screen">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						className="text-center"
					>
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
						<p className="text-gray-600 font-karla">Loading your profile...</p>
					</motion.div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100">
			{/* Sticky Header */}
			<div className="border-b border-white/20 backdrop-blur-md">
				<div className="max-w-7xl mx-auto p-6">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
					>
						<div className="flex items-center justify-between">
							<div>
								<h1 className="font-light text-black font-abc-prophet text-4xl leading-tight lg:text-[58px] lg:leading-[76px] mb-4 text-center lg:text-left">
									Profile Management
								</h1>
								<p className="text-xl lg:text-[24px] text-black font-karla lg:leading-9 text-center lg:text-left">
									Manage your doula profile and listing information
								</p>
							</div>
							<div className="flex items-center space-x-4">
								<Button
									onClick={() => navigate(`/doula-profile/${listing?.id}`)}
									variant="outline"
									className="border-newTheme-lightGrey text-black hover:bg-gray-50 shadow-btn-shadow hover:text-black"
								>
									<ExternalLink className="h-4 w-4 mr-2" />
									View Public Profile
								</Button>
								<Button
									onClick={handleSaveProfile}
									disabled={saving}
									className="bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow"
								>
									{saving ? "Saving..." : "Save Changes"}
								</Button>
							</div>
						</div>
					</motion.div>
				</div>
			</div>

			{/* Main Content */}
			<div className="max-w-7xl mx-auto p-6">
				{/* Status Card */}
				<StatusCard
					listing={listing}
					onRemoveListing={() => {
						if (listing) {
							setListing((prev) =>
								prev ? { ...prev, status: "suspended" } : null,
							);
						}
					}}
				/>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
					{/* Personal Information */}
					<PersonalInformationCard
						listing={listing}
						userInfo={userInfo}
						uploadingPhoto={uploadingPhoto}
						onPhotoUpload={handlePhotoUpload}
						onUserInfoChange={(field, value) => {
							setUserInfo((prev) =>
								prev ? { ...prev, [field]: value } : null,
							);
						}}
						onListingChange={(field, value) => {
							setListing((prev) => (prev ? { ...prev, [field]: value } : null));
						}}
					/>

					{/* Professional Information */}
					<ProfessionalInformationCard
						listing={listing}
						onListingChange={(field, value) => {
							setListing((prev) => (prev ? { ...prev, [field]: value } : null));
						}}
					/>
				</div>

				{/* Profile Preferences Section */}
				<ProfilePreferencesCard
					listing={listing}
					onListingChange={(field, value) => {
						setListing((prev) => (prev ? { ...prev, [field]: value } : null));
					}}
				/>

				{/* Integrations Section */}
				<IntegrationsCard
					listing={listing}
					onGoogleCalendarConnect={() => {
						// TODO: Implement Google Calendar connection
					}}
					onStripeConnect={() => {
						// TODO: Implement Stripe Connect
					}}
				/>

				{/* Packages & Services */}
				<PackagesCard
					packages={packages}
					newPackage={newPackage}
					onNewPackageChange={(field, value) => {
						setNewPackage((prev) => ({ ...prev, [field]: value }));
					}}
					onAddPackage={() => {
						// TODO: Implement add package via edge function
					}}
					onEditPackage={(pkg) => {
						setEditingPackage(pkg);
						setEditDialogOpen(true);
					}}
					onDeletePackage={(id, name) => {
						setItemToDelete({ type: "package", id, name });
						setDeleteDialogOpen(true);
					}}
					formatPrice={(value) => {
						// Simple price formatting
						const number = parseFloat(value.replace(/[^0-9.]/g, "")) || 0;
						return number.toFixed(2);
					}}
				/>

				{/* Certifications */}
				<CertificationsCard
					certifications={certifications}
					newCertification={newCertification}
					uploadingCertificate={uploadingCertificate}
					onNewCertificationChange={(field, value) => {
						setNewCertification((prev) => ({ ...prev, [field]: value }));
					}}
					onAddCertification={() => {
						// TODO: Implement add certification via edge function
					}}
					onEditCertification={(cert) => {
						setEditingCertification(cert);
						setEditCertDialogOpen(true);
					}}
					onDeleteCertification={(id, name) => {
						setItemToDelete({ type: "certification", id, name });
						setDeleteDialogOpen(true);
					}}
					onCertificateUpload={(event) => {
						// TODO: Implement certificate upload
					}}
				/>

				{/* Availability Settings */}
				<AvailabilityCard
					listing={listing}
					monthsAvailable={monthsAvailable}
					savingMonths={savingMonths}
					onListingChange={(field, value) => {
						setListing((prev) => (prev ? { ...prev, [field]: value } : null));
					}}
					onMonthToggle={(month) => {
						setMonthsAvailable((prev) => ({
							...prev,
							[month]: !prev[month as keyof typeof prev],
						}));
					}}
					onToggleSelectAllMonths={toggleSelectAllMonths}
					onSaveMonthsAvailable={async () => {
						if (!listing) return;

						try {
							setSavingMonths(true);

							// Update listing with new months available
							const updatedListing = {
								...listing,
								months_available: monthsAvailable,
							};

							const response = await fetch(
								`${VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
								{
									method: "POST",
									headers: {
										"Content-Type": "application/json",
										Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
									},
									body: JSON.stringify({
										step: "profile_update",
										data: {
											months_available: monthsAvailable,
											availability_guardrails: introDays,
											booking_window_months: listing.booking_window_months || 6,
										},
									}),
								},
							);

							const result = await response.json();
							if (!result.success) throw new Error(result.error);

							setListing(updatedListing);
							alert("Availability updated successfully!");
						} catch (error) {
							console.error("Error saving availability:", error);
							alert("Failed to save availability. Please try again.");
						} finally {
							setSavingMonths(false);
						}
					}}
					introDays={introDays}
					onIntroDayToggle={(day) => {
						setIntroDays((prev) => ({
							...prev,
							[day]: {
								...prev[day as keyof typeof prev],
								available: !prev[day as keyof typeof prev].available,
							},
						}));
					}}
					onIntroTimeChange={(day, field, value) => {
						setIntroDays((prev) => ({
							...prev,
							[day]: {
								...prev[day as keyof typeof prev],
								[field]: value,
							},
						}));
					}}
				/>

				{/* Provider Exclusions */}
				<ProviderExclusionsCard
					excludedProviders={excludedProviders}
					onAddProvider={handleAddProvider}
					onRemoveProvider={handleRemoveProvider}
					inputRef={inputRef}
					getProviderTypeLabel={(types) => {
						if (!types || types.length === 0) return "Healthcare Provider";
						if (types.includes("hospital")) return "Hospital";
						if (types.includes("doctor")) return "Medical Practice";
						if (types.includes("health")) return "Healthcare Facility";
						return types[0]
							.replace(/_/g, " ")
							.replace(/\b\w/g, (l) => l.toUpperCase());
					}}
				/>

				{/* Delete Confirmation Dialog */}
				<Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Confirm Deletion</DialogTitle>
							<DialogDescription>
								Are you sure you want to delete "{itemToDelete?.name}"? This
								action cannot be undone.
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setDeleteDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button
								variant="destructive"
								onClick={() => {
									// TODO: Implement actual deletion via edge function

									setDeleteDialogOpen(false);
									setItemToDelete(null);
								}}
							>
								Delete
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

			</div>
		</div>
	);
}
